<template>
  <Head title="Company" />

  <div class="mx-auto w-11/12 max-w-[1200px] space-y-6 py-6">
    <!-- Header Card -->
    <CompanyHeaderCard
      :company="companyView"
      :is-admin="isAdmin"
      @edit="showEditDrawer = true"
    />

    <!-- Agents -->
    <AgentsCard
      :agents="agents"
      :is-admin="isAdmin"
      :company-id="company.id"
      @setup="openSetupDrawer"
      @manage="openManageDrawer"
    />

    <!-- Members -->
    <MembersCard
      :members="members"
      :is-admin="isAdmin"
      @invite="showInviteDrawer = true"
      @manage-member="openMemberManageDrawer"
    />

    <!-- Agent Drawers -->
    <AgentSetupDrawer
      v-model:visible="showSetupDrawer"
      :company-id="company.id"
      @continue="onSetupContinue"
    />

    <AgentPairingDrawer
      v-model:visible="showPairingDrawer"
      :code="pairingCode"
      :expires-at="pairingExpiresAt"
      :company-id="company.id"
      @paired="onPaired"
      @cancel="onSetupCancelled"
    />

    <AgentConfigureDrawer
      v-model:visible="showConfigureDrawer"
      :registration="activeRegistration"
      :company-id="company.id"
      :initial-server-url="configInitialUrl"
      :initial-profile="configInitialProfile"
      @configured="onConfigured"
      @cancel="onSetupCancelled"
    />

    <AgentManageDrawer
      v-model:visible="showManageDrawer"
      :registration="activeRegistration"
      :is-admin="isAdmin"
      @reconfigure="onReconfigure"
      @revoked="onRevoked"
      @updated="onAgentUpdated"
    />

    <!-- Company + Member Drawers -->
    <CompanyEditDrawer
      v-model:visible="showEditDrawer"
      :company="companyView"
      @updated="onCompanyUpdated"
    />

    <InviteMemberDrawer
      v-model:visible="showInviteDrawer"
      :company-id="company.id"
      @invited="onMemberInvited"
    />

    <MemberManageDrawer
      v-model:visible="showMemberManageDrawer"
      :member="activeMember"
      @updated="onMemberUpdated"
      @removed="onMemberRemoved"
    />
  </div>
</template>

<script setup lang="ts">
import { Head, router } from "@inertiajs/vue3"
import { computed, onMounted, onUnmounted, ref } from "vue"

import AgentConfigureDrawer from "../../components/company/AgentConfigureDrawer.vue"
import AgentManageDrawer from "../../components/company/AgentManageDrawer.vue"
import AgentPairingDrawer from "../../components/company/AgentPairingDrawer.vue"
import AgentSetupDrawer, {
  type PairingCodeResult,
} from "../../components/company/AgentSetupDrawer.vue"
import AgentsCard from "../../components/company/AgentsCard.vue"
import CompanyEditDrawer from "../../components/company/CompanyEditDrawer.vue"
import CompanyHeaderCard from "../../components/company/CompanyHeaderCard.vue"
import InviteMemberDrawer from "../../components/company/InviteMemberDrawer.vue"
import MemberManageDrawer from "../../components/company/MemberManageDrawer.vue"
import MembersCard from "../../components/company/MembersCard.vue"
import { subscribeToCompany, type AblySubscription } from "../../services/cable"
import type {
  AgentRegistrationType,
  CompanyType,
  CurrentMemberType,
  MemberType,
} from "./types"

type AgentRegistrationUpdatedPayload = {
  type: string
  agent_registration_id: string
  agent_registration_gid: string
  status: string
  name?: string | null
  hostname?: string | null
  ip_address?: string | null
  agent_version?: string | null
  last_heartbeat_at?: string | null
}

// ─── Props ──────────────────────────────────────────────────────

interface Props {
  company: CompanyType
  currentMember?: CurrentMemberType | null
}

const props = defineProps<Props>()

// Local mutable copies to avoid prop mutation
const agents = ref<AgentRegistrationType[]>([
  ...props.company.agentRegistrations,
])
const members = ref<MemberType[]>([...props.company.members])

// Provide a reactive view of company with local agents
const companyView = computed<CompanyType>(() => ({
  ...props.company,
  agentRegistrations: agents.value,
  members: members.value,
}))

const isAdmin = computed(
  () => props.currentMember?.role?.toUpperCase() === "ADMIN",
)

// ─── Agent Drawer State ─────────────────────────────────────────

const showSetupDrawer = ref(false)
const showPairingDrawer = ref(false)
const showConfigureDrawer = ref(false)
const showManageDrawer = ref(false)
const activeRegistration = ref<AgentRegistrationType | null>(null)
const pairingCode = ref<string | null>(null)
const pairingExpiresAt = ref<string | null>(null)
const configInitialUrl = ref("")
const configInitialProfile = ref("")

// ─── Company + Member Drawer State ──────────────────────────────

const showEditDrawer = ref(false)
const showInviteDrawer = ref(false)
const showMemberManageDrawer = ref(false)
const activeMember = ref<MemberType | null>(null)

// ─── Agent Setup Flow ───────────────────────────────────────────

const openSetupDrawer = () => {
  showSetupDrawer.value = true
}

const onSetupContinue = (result: PairingCodeResult) => {
  pairingCode.value = result.code
  pairingExpiresAt.value = result.expiresAt
  showSetupDrawer.value = false
  showPairingDrawer.value = true
}

const onPaired = (registration: AgentRegistrationType) => {
  activeRegistration.value = registration
  agents.value.push(registration)
  showPairingDrawer.value = false
  showConfigureDrawer.value = true
}

const onConfigured = () => {
  showConfigureDrawer.value = false
  activeRegistration.value = null
  router.reload()
}

const onSetupCancelled = () => {
  showPairingDrawer.value = false
  showConfigureDrawer.value = false
  pairingCode.value = null
  pairingExpiresAt.value = null
  activeRegistration.value = null
}

// ─── Agent Manage Flow ──────────────────────────────────────────

const openManageDrawer = (agent: AgentRegistrationType) => {
  activeRegistration.value = agent
  showManageDrawer.value = true
}

const onReconfigure = (registration: AgentRegistrationType) => {
  activeRegistration.value = registration
  showManageDrawer.value = false
  configInitialUrl.value = ""
  configInitialProfile.value = ""
  showConfigureDrawer.value = true
}

const onRevoked = () => {
  const revokedId = activeRegistration.value?.id
  showManageDrawer.value = false
  activeRegistration.value = null
  if (revokedId) {
    agents.value = agents.value.filter((a) => a.id !== revokedId)
  }
}

const onAgentUpdated = (registration: AgentRegistrationType) => {
  activeRegistration.value = registration
  const idx = agents.value.findIndex((a) => a.id === registration.id)
  if (idx >= 0) {
    agents.value[idx] = registration
  }
}

// ─── Company Edit Flow ──────────────────────────────────────────

const onCompanyUpdated = () => {
  router.reload()
}

// ─── Member Management Flow ─────────────────────────────────────

const openMemberManageDrawer = (member: MemberType) => {
  activeMember.value = member
  showMemberManageDrawer.value = true
}

const onMemberInvited = () => {
  router.reload()
}

const onMemberUpdated = (member: MemberType) => {
  const idx = members.value.findIndex((m) => m.id === member.id)
  if (idx >= 0) {
    members.value[idx] = member
  }
  activeMember.value = null
}

const onMemberRemoved = () => {
  showMemberManageDrawer.value = false
  activeMember.value = null
  router.reload()
}

// ─── WebSocket Subscription ─────────────────────────────────────

let subscription: AblySubscription | null = null

const mergePayloadIntoAgent = (
  existing: AgentRegistrationType,
  payload: AgentRegistrationUpdatedPayload,
): AgentRegistrationType => ({
  ...existing,
  status: payload.status,
  name: payload.name ?? existing.name,
  hostname: payload.hostname ?? existing.hostname,
  ipAddress: payload.ip_address ?? existing.ipAddress,
  agentVersion: payload.agent_version ?? existing.agentVersion,
  lastHeartbeatAt: payload.last_heartbeat_at ?? existing.lastHeartbeatAt,
})

const startSubscription = async () => {
  if (subscription) return

  subscription = await subscribeToCompany<AgentRegistrationUpdatedPayload>(
    props.company.id,
    (data) => {
      if (data?.type !== "agent_registration_updated") return

      const regId = data.agent_registration_id
      if (!regId) return

      // Update in agent list
      const idx = agents.value.findIndex((a) => a.id === regId)
      if (idx >= 0) {
        agents.value[idx] = mergePayloadIntoAgent(agents.value[idx], data)
      }

      // Update active registration if it matches
      if (activeRegistration.value?.id === regId) {
        activeRegistration.value = mergePayloadIntoAgent(
          activeRegistration.value,
          data,
        )
      }
    },
  )
}

const stopSubscription = () => {
  if (subscription) {
    subscription.close()
    subscription = null
  }
}

onMounted(() => {
  void startSubscription()
})

onUnmounted(() => {
  stopSubscription()
})
</script>
